/**
 * TransformationsSection - Transformation insights for L3 users
 *
 * Extracted from ModernResultsFull.tsx for Phase 3 Mobile Optimization.
 * Tier-gated: Only visible for tierLevel >= 3 (DramaX)
 *
 * Phase 3.4: Optional accordion behavior (A/B test via l3_accordions_enabled flag)
 */

import { View, Pressable, StyleSheet } from 'react-native';
import { TierBody, TierHeadline } from '@dramadetector/ui';
import type { Transformation } from '@dramadetector/shared';

import { captureEvent } from '../../../lib/analytics';
import { useSectionTruncation, useCollapsibleState, TRUNCATION_DEFAULTS, COLLAPSIBLE_DEFAULTS } from '../hooks';
import { sharedStyles } from '../sharedStyles';
import { AccordionHeader } from './AccordionHeader';
import { useTransformationInsightFlag, useRewritesEnabledFlag } from '../../../hooks/useFeatureFlags';

interface TransformationCardProps {
  transformation: Transformation;
  t: (key: string, options?: Record<string, unknown>) => string;
}

/**
 * Individual transformation card display
 */
function TransformationCard({ transformation, t }: TransformationCardProps) {
  const impactLabel =
    transformation.impactScore >= 7
      ? t('home.modern.impact_high', { defaultValue: 'High Impact' })
      : transformation.impactScore >= 4
      ? t('home.modern.impact_moderate', { defaultValue: 'Moderate Impact' })
      : t('home.modern.impact_low', { defaultValue: 'Low Impact' });

  return (
    <View style={sharedStyles.featureCard}>
      {/* Tier Badge */}
      <View style={[sharedStyles.tierBadge, { backgroundColor: 'rgba(236, 72, 153, 0.1)' }]}>
        <TierBody size="xs" style={{ color: '#DB2777', fontWeight: '600' }}>
          {t('common.tier_dramax', { defaultValue: 'DramaX' })}
        </TierBody>
      </View>

      {/* Transformation Type */}
      <TierBody size="sm" color="text-gray-800" style={{ fontWeight: '600', marginBottom: 4 }}>
        {(transformation.transformationType || 'Transformation')
          .replace(/([A-Z])/g, ' $1')
          .trim()
          .replace(/\b\w/g, (c) => c.toUpperCase())}
      </TierBody>

      {/* Impact Score */}
      <TierBody size="xs" color="text-pink-600" style={{ marginBottom: 8 }}>
        {impactLabel} ({transformation.impactScore}/10)
      </TierBody>

      {/* Before */}
      <View style={[sharedStyles.snippetBox, sharedStyles.beforeBox]}>
        <TierBody size="xs" color="text-gray-500" style={{ marginBottom: 4 }}>
          {t('home.modern.before', { defaultValue: 'Before:' })}
        </TierBody>
        <TierBody size="sm" color="text-gray-700">
          "{transformation.beforeText}"
        </TierBody>
      </View>

      {/* After */}
      <View style={[sharedStyles.snippetBox, sharedStyles.afterBox, { marginTop: 8 }]}>
        <TierBody size="xs" color="text-gray-500" style={{ marginBottom: 4 }}>
          {t('home.modern.after', { defaultValue: 'After:' })}
        </TierBody>
        <TierBody size="sm" color="text-gray-700">
          "{transformation.afterText}"
        </TierBody>
      </View>

      {/* Insights */}
      {transformation.insights && transformation.insights.length > 0 && (
        <View style={{ marginTop: 12 }}>
          <TierBody size="xs" color="text-gray-500" style={{ marginBottom: 4 }}>
            {t('home.modern.insights', { defaultValue: 'Key Insights:' })}
          </TierBody>
          {transformation.insights.slice(0, 3).map((insight, i) => (
            <TierBody key={i} size="xs" color="text-gray-600" style={{ marginLeft: 8 }}>
              • {insight}
            </TierBody>
          ))}
        </View>
      )}
    </View>
  );
}

export interface TransformationsSectionProps {
  /** List of transformations to display */
  transformations: Transformation[];
  /** Translation function */
  t: (key: string, options?: Record<string, unknown>) => string;
  /** Test ID for testing */
  testID?: string;
  /** Maximum items before truncation (default: 2) */
  maxVisible?: number;
  /** Enable accordion behavior (A/B test: l3_accordions_enabled flag) */
  accordionEnabled?: boolean;
}

/**
 * Transformations section with header and list (L3 only)
 * Supports truncation with "Show more/less" toggle
 * Phase 3.4: Optional accordion behavior when accordionEnabled=true
 */
export function TransformationsSection({
  transformations,
  t,
  testID,
  maxVisible = TRUNCATION_DEFAULTS.transformations,
  accordionEnabled = false,
}: TransformationsSectionProps) {
  const isEnabled = useTransformationInsightFlag();
  const rewritesEnabled = useRewritesEnabledFlag();
  const { showAll, toggle, isTruncated, visibleCount, hiddenCount } = useSectionTruncation({
    totalItems: transformations.length,
    maxVisible,
  });

  const { isExpanded, toggle: toggleAccordion } = useCollapsibleState({
    defaultExpanded: COLLAPSIBLE_DEFAULTS.transformations,
    animated: true,
    sectionId: 'transformations',
  });

  if (isEnabled === false) return null;
  if (!rewritesEnabled) return null;

  if (!transformations || transformations.length === 0) {
    return null;
  }

  const visibleTransformations = showAll ? transformations : transformations.slice(0, visibleCount);

  const handleToggle = () => {
    captureEvent('Section_Toggle', {
      section: 'transformations',
      action: showAll ? 'collapse' : 'expand',
      total_items: transformations.length,
      visible_items: visibleCount,
    });
    toggle();
  };

  // Accordion mode: collapsible header with full content
  if (accordionEnabled) {
    return (
      <View testID={testID}>
        <AccordionHeader
          title={t('home.modern.transformation_insights', { defaultValue: 'Transformation Insights' })}
          itemCount={transformations.length}
          isExpanded={isExpanded}
          onToggle={toggleAccordion}
          sectionId="transformations"
          t={t}
          testID={`${testID}-accordion-header`}
        />
        {isExpanded && (
          <>
            {visibleTransformations.map((transformation, index) => (
              <TransformationCard
                key={`transformation-${index}`}
                transformation={transformation}
                t={t}
              />
            ))}
            {/* Show more/less toggle (within accordion) */}
            {isTruncated && (
              <Pressable
                onPress={handleToggle}
                style={styles.toggleButton}
                accessibilityRole="button"
                accessibilityLabel={
                  showAll
                    ? t('home.modern.show_less', { defaultValue: 'Show less' })
                    : t('home.modern.show_more', { defaultValue: 'Show {count} more', count: hiddenCount })
                }
                testID={`${testID}-toggle`}
              >
                <TierBody size="sm" style={styles.toggleText}>
                  {showAll
                    ? t('home.modern.show_less', { defaultValue: 'Show less' })
                    : t('home.modern.show_more', { defaultValue: 'Show {count} more', count: hiddenCount })}
                </TierBody>
              </Pressable>
            )}
          </>
        )}
      </View>
    );
  }

  // Default mode: static header with truncation
  return (
    <View testID={testID}>
      <View style={[sharedStyles.sectionHeader, { maxWidth: 400, alignSelf: 'center', width: '100%' }]}>
        <TierHeadline size="lg" weight="semibold" color="text-white" as="h3">
          {t('home.modern.transformation_insights', { defaultValue: 'Transformation Insights' })}
        </TierHeadline>
      </View>
      {visibleTransformations.map((transformation, index) => (
        <TransformationCard
          key={`transformation-${index}`}
          transformation={transformation}
          t={t}
        />
      ))}
      {/* Show more/less toggle */}
      {isTruncated && (
        <Pressable
          onPress={handleToggle}
          style={styles.toggleButton}
          accessibilityRole="button"
          accessibilityLabel={
            showAll
              ? t('home.modern.show_less', { defaultValue: 'Show less' })
              : t('home.modern.show_more', { defaultValue: 'Show {count} more', count: hiddenCount })
          }
          testID={`${testID}-toggle`}
        >
          <TierBody size="sm" style={styles.toggleText}>
            {showAll
              ? t('home.modern.show_less', { defaultValue: 'Show less' })
              : t('home.modern.show_more', { defaultValue: 'Show {count} more', count: hiddenCount })}
          </TierBody>
        </Pressable>
      )}
    </View>
  );
}

/**
 * StyleSheet - WCAG AAA compliant
 * - Touch target: min 44x44 points
 * - Contrast: purple-800 #5B21B6 on white = 9.4:1
 */
const styles = StyleSheet.create({
  toggleButton: {
    paddingVertical: 14,
    minHeight: 44, // WCAG touch target
    alignItems: 'center',
    justifyContent: 'center',
  },
  toggleText: {
    color: '#5B21B6', // purple-800 (WCAG AAA: 9.4:1 contrast)
    fontWeight: '600',
  },
});
